/* eslint-disable @typescript-eslint/no-explicit-any */
import ajax from '@client/components/ajax';
import { muiForm } from '@client/new/helpers/forms';
import { Actions, Async, FSpread, SMap } from '@client/new/helpers/types';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  GetWorkoutByIdQuery,
  Tag,
  WorkoutTiny,
  useGetProgramByIdQuery,
  useGetWorkoutByIdQuery,
  useUnpublishWorkoutByIdMutation,
} from 'generated/graphql';
import { useEffect, useState } from 'react';
import {
  Control,
  FieldErrors,
  FieldPath,
  UseFormWatch,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';
import {
  emptyOutdoorSet,
  emptyTreadmillSet,
  getPayloadFromValues,
  initTemplates,
  initWorkout,
  workoutFormData,
} from './mappers';
import {
  RunningExerciseType,
  BuilderType,
  Template,
  WorkoutItem,
  Media,
  MeasurementMode,
} from './workout.domain';
import { isNil, omit, set } from 'lodash';
import uuid4 from 'uuid4';
import { validatePublish } from '@client/new/pages/workout/models/validators';
import { LibraryVideo } from './useVideos';
import { toImgSrcRaw, toVideoSrc } from '@client/new/common/media.helpers';
import { redirectFromWorkouts } from './redirect';
import { Program } from '@playbook-technologies/common-enums';
import auth from '@client/components/auth';
import {
  useWorkoutModel,
  WorkoutModel,
} from '../../workout-editor/model/workout.model';
import { addIds } from './workoutEdit.model.helpers';
import { ParentExerciseType } from '../../workout-editor/model/workout.domain';
import { _noop } from '@client/new/helpers/general';
import {
  useExerciseSuggestionsModel,
  ExerciseSuggestion,
} from '../../creator/library/models/exercise-suggestions.model';
import { trackWorkoutEvent } from '../../workout-editor/view-model/workout.tracking';
import { trackEvent } from '@services/tracking';

export type PayloadMap = {
  save: never;
  extendedSave: LegacyWorkoutFormValues;
  extendedPublish: LegacyWorkoutFormValues;
  setErrors: SMap<string>;

  publish: never;
  unpublish: never;
  archive: never;
  removeIntro: never;
  removeThumbnail: never;
  uploadMedia: File;
  addWorkoutItem: Template;
  addSet: number;
  addCircuitSet: { circuitIndex: number; index: number };
  removeSet: { exercise: number; set: number };
  removeCircuitSet: { circuitIndex: number; exercise: number; set: number };
  removeCircuitItem: { circuitIndex: number; exerciseIndex: number };
  removeWorkoutItem: number;
  changeOrderInWorkout: { index: number; order: number };
  changeOrderInCircuit: { circuitIndex: number; index: number; order: number };
  addMedia: {
    media: Media;
    index: number;
    circuitIndex: number | null;
    parentId?: string;
    subExerciseId?: string;
  };
  removeMedia: { index: number; isYoutube?: boolean; circuitIndex?: number };
  addWorkoutCircuitItem: number;
  setTimeZone: string;
  setPublishedAt: string;
  clearSchedule: never;
  addRunCompleteAudio: { index: number; uuid: string; file: File };
  addSetAudio: {
    exerciseIndex: number;
    uuid: string;
    setIndex: number;
    file: File;
  };
  addOutdoorSet: number;
  addTreadmillSet: number;
  addFromClipboard: WorkoutItem[];
  changeRunningVariant: { index: number; variant: RunningExerciseType };
  changeMeasurementMode: { index: number; measurementMode: MeasurementMode };
  removeSetAudio: {
    exerciseIndex: number;
    setIndex: number;
  };
  removeRunCompleteAudio: number;
  clearRecommendations: never;
  setExerciseSearch: string;
  exerciseTitleUpdate: {
    circuitIndex?: number;
    index: number;
    description: string;
    uuid: string;
  };
  applyHint: { circuitIndex?: number; index: number; hint: Hint; uuid: string };
  addWorkoutItemsWithVideoIds: {
    videos: LibraryVideo[];
    circuitIndex: number | null;
    parentId?: string;
  };
  addTemplateWithMedia: {
    media: Media;
    circuitIndex: number | null;
    parentId?: string;
  };
  AddAutoplayed: {
    type: ParentExerciseType;
    time: number;
    rest: number;
    sets?: number;
    rounds?: number;
  };
};
export type EditWorkoutModelAction = Actions<PayloadMap>;
export type EditWorkoutModelDispatcher = FSpread<PayloadMap>;

export type DelegatedActionMap = {
  addAutoplayed: PayloadMap['AddAutoplayed'] & { id: string };
  updateSubExerciseMedia: {
    media: Media;
    index: number;
    parentId?: string;
    subExerciseId?: string;
  };
  addSubExercises: { parentId: string; media: Media[] };
  removeExercise: { exerciseId: string };
  save: LegacyWorkoutFormValues;
  publish: LegacyWorkoutFormValues;
  schedule: LegacyWorkoutFormValues;
};
export type DelegatedAction = Actions<DelegatedActionMap>;

const validationSchema = z.object({
  name: z.string().nonempty({ message: 'Title is required' }),
});

export type Hint = { title?: string; description?: string };
type InputHints = Record<string, Hint>;

export type FormErrors = FieldErrors<LegacyWorkoutFormValues>;

export type EditWorkoutModel = {
  dispatch: EditWorkoutModelDispatcher;
  workoutInfo: any;
  control: Control<LegacyWorkoutFormValues, any, LegacyWorkoutFormValues>;
  submitting: boolean;
  intro: {
    src: string | null;
    loading: boolean;
  };
  thumbnail: {
    src: string | null;
    loading: boolean;
  };
  exercises: WorkoutItem[];
  watch: UseFormWatch<LegacyWorkoutFormValues>;
  inputHints: InputHints;
  readonly: boolean;
  disableArchiveAction: boolean;
  formErrors: FormErrors;
  newWorkoutErrors: SMap<string>;
  refinedWorkoutModel: Async<WorkoutModel>;
  setDispatchCb: (v: () => (action: DelegatedAction) => void) => void;
  getValues: () => LegacyWorkoutFormValues;
  exerciseSuggestions: ExerciseSuggestion[];
};

export type LegacyWorkoutFormValues = {
  name: string;
  description?: string;
  duration?: number;
  messagePush?: string;
  introVideo?: Media;
  thumbnail?: Media;
  programId?: number;
  freeSample: boolean;
  id: string;
  requiredEquipment?: string;
  exercises: any;
  mediaExercise?: Media;
  tags: Tag[];
  publishedAt?: string;
  timeZone?: string;
};

export const useEditWorkoutModel = ({
  influencerId,
  workoutId,
  builderType,
  locationQuery,
}: {
  influencerId: number;
  workoutId?: number;
  builderType: BuilderType;
  locationQuery: Record<string, string>;
}): Async<EditWorkoutModel> => {
  const [dispatchToRefinedModel, setDispatchToRefinedModel] =
    useState<(action: DelegatedAction) => void>(_noop);
  const locationProgramId = locationQuery?.programId
    ? Number(locationQuery.programId)
    : undefined;
  const isClass = builderType == 'class';
  const workoutQuery = useGetWorkoutByIdQuery({
    variables: {
      id: workoutId || 0,
    },
    skip: !workoutId || workoutId === 0,
    fetchPolicy: 'network-only',
  });
  const { loading, error } = workoutQuery;
  const [data, setDataWithIds] = useState<GetWorkoutByIdQuery | undefined>(
    undefined,
  );

  useEffect(() => {
    addIds(workoutQuery.data);
    if (!workoutQuery.data || !workoutQuery.data.workout?.exercises) return;
    setDataWithIds(workoutQuery.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(workoutQuery.data)]);

  const programId = workoutQuery.data?.workout?.programId;
  const { data: programData, loading: programLoading } = useGetProgramByIdQuery(
    {
      variables: { id: programId || 0 },
      skip: !programId,
      fetchPolicy: 'network-only',
    },
  );

  const [unpublishWorkout] = useUnpublishWorkoutByIdMutation({
    variables: {
      id: workoutId || 0,
    },
  });

  const refinedWorkoutModel = useWorkoutModel({
    data: { ...workoutQuery, data },
  });
  const [workoutInfoUpload, setWorkoutInfoUpload] = useState<{
    image: boolean;
    video: boolean;
  }>({
    image: false,
    video: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [inputHints, setInputHints] = useState<InputHints>({});
  const [exerciseSearch, setExerciseSearch] = useState<string>('');
  const {
    control,
    reset,
    clearErrors,
    getValues,
    handleSubmit,
    watch,
    setValue,
    formState,
  } = muiForm(
    useForm<LegacyWorkoutFormValues>({
      defaultValues: initWorkout(builderType, locationProgramId),
      resolver: zodResolver(validationSchema),
    }),
  );
  const { exerciseSuggestions } = useExerciseSuggestionsModel(exerciseSearch);
  const { replace, update } = useFieldArray({
    name: 'exercises',
    control,
  });

  const [errors, setErrors] = useState<SMap<string>>({});

  useEffect(() => {
    if (data?.workout) {
      reset(workoutFormData(data.workout as WorkoutTiny));
    }
  }, [data, reset]);

  if (loading || programLoading) {
    return {
      type: 'fetching',
    };
  }

  if (error) {
    return {
      type: 'fetch-error',
      value: 'Could not fetch data',
    };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const dispatch: EditWorkoutModelDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as EditWorkoutModelAction;
    switch (action.type) {
      case 'AddAutoplayed': {
        const id = uuid4();
        const nextExercises = [
          ...(getValues().exercises || []),
          { ...initTemplates[action.payload.type], uuid: id, id },
        ];
        setValue('exercises', nextExercises);
        clearErrors();
        dispatchToRefinedModel({
          type: 'addAutoplayed',
          payload: { ...action.payload, id },
        });
        return null;
      }
      case 'save':
        dispatchToRefinedModel({ type: 'save', payload: getValues() });
        return null;
      case 'extendedSave':
        extendedSave(action.payload)();
        return null;
      case 'setErrors':
        handleSubmit(() => {
          setErrors(action.payload);
        })();
        return null;
      case 'publish':
        dispatchToRefinedModel({ type: 'publish', payload: getValues() });
        return null;
      case 'extendedPublish':
        extendedPublish(action.payload)();
        return null;
      case 'unpublish':
        unpublish();
        return null;
      case 'archive':
        archive();
        return null;
      case 'removeIntro':
        setValue('introVideo', undefined);
        return null;
      case 'removeThumbnail':
        setValue('thumbnail', undefined);
        return null;
      case 'uploadMedia':
        uploadMedia(action.payload);
        return null;
      case 'addWorkoutItem': {
        const data: any = initTemplates[action.payload];
        setValue('exercises', [
          ...(getValues().exercises || []),
          { ...data, uuid: uuid4() },
        ]);
        clearErrors();

        if (data.name === 'Running') {
          trackEvent({
            type: 'Running Template Selected',
            payload: { type: data.variant },
          });
        }
        return null;
      }
      case 'addWorkoutItemsWithVideoIds':
        addTemplatesFromVideoIds(action.payload);
        return null;
      case 'addTemplateWithMedia':
        addTemplateWithMedia(action.payload);
        return null;
      case 'addWorkoutCircuitItem': {
        const WorkoutItems = getValues().exercises || [];
        const mapped = WorkoutItems.map((e: { exercises: any }, i: number) =>
          i === action.payload
            ? {
                ...e,
                exercises: [
                  ...e.exercises,
                  { ...initTemplates.exercise, uuid: uuid4() },
                ],
              }
            : e,
        );
        replace(mapped);
        clearErrors(`exercises.${action.payload}`);
        return null;
      }
      case 'changeOrderInCircuit': {
        const { circuitIndex, index, order } = action.payload;
        const circuit = getValues().exercises[circuitIndex];
        const arr = circuit.exercises || [];
        [arr[index], arr[index + order]] = [arr[index + order], arr[index]];
        update(circuitIndex, { ...circuit, exercises: arr });
        return null;
      }
      case 'addCircuitSet': {
        const { circuitIndex, index } = action.payload;
        const circuit = getValues().exercises[circuitIndex];
        const mappedExercises = circuit.exercises.map(
          (e: { sets: string | any[] }, i: number) => {
            if (i !== index) {
              return e;
            }

            const previousSetUnit =
              e.sets.length > 0 ? e.sets[0].reps[1] : 'reps';
            return {
              ...e,
              sets: [
                ...e.sets,
                { reps: ['0', previousSetUnit], uuid: uuid4() },
              ],
            };
          },
        );
        update(circuitIndex, { ...circuit, exercises: mappedExercises });
        return null;
      }
      case 'removeCircuitSet': {
        const { circuitIndex, exercise, set } = action.payload;
        const circuit = getValues().exercises[circuitIndex];
        const mappedExercises = circuit.exercises.map(
          (e: { sets: any[] }, i: number) =>
            i !== exercise
              ? e
              : {
                  ...e,
                  sets: e.sets.filter((s: any, idx: number) => idx !== set),
                },
        );
        update(circuitIndex, { ...circuit, exercises: mappedExercises });
        return null;
      }
      case 'addSet':
        setValue(
          'exercises',
          getValues().exercises.map(
            (e: { sets: string | any[] }, i: number) => {
              if (i !== action.payload) {
                return e;
              }
              const previousSetUnit =
                e.sets.length > 0 ? e.sets[0].reps[1] : 'reps';
              return {
                ...e,
                sets: [...(e.sets || []), { reps: ['0', previousSetUnit] }],
                uuid: uuid4(),
              };
            },
          ),
        );
        return null;
      case 'addTreadmillSet': {
        setValue(
          'exercises',
          getValues().exercises.map((e: { sets: any }, i: number) =>
            i !== action.payload
              ? e
              : {
                  ...e,
                  sets: [
                    ...(e.sets || []),
                    { ...emptyTreadmillSet, uuid: uuid4() },
                  ],
                  uuid: uuid4(),
                },
          ),
        );
        return null;
      }
      case 'addOutdoorSet': {
        setValue(
          'exercises',
          getValues().exercises.map((e: { sets: any }, i: number) =>
            i !== action.payload
              ? e
              : {
                  ...e,
                  sets: [
                    ...(e.sets || []),
                    { ...emptyOutdoorSet, uuid: uuid4() },
                  ],
                  uuid: uuid4(),
                },
          ),
        );
        return null;
      }

      case 'changeRunningVariant':
        setValue(
          'exercises',
          getValues().exercises.map((e: any, i: number) =>
            i !== action.payload.index
              ? e
              : {
                  ...e,
                  variant: action.payload.variant,
                  sets: [
                    action.payload.variant === RunningExerciseType.Outdoor
                      ? emptyOutdoorSet
                      : emptyTreadmillSet,
                  ],
                  uuid: uuid4(),
                },
          ),
        );
        return null;
      case 'changeMeasurementMode':
        setValue(
          'exercises',
          getValues().exercises.map((e: any, i: number) =>
            i !== action.payload.index
              ? e
              : { ...e, measurementMode: action.payload.measurementMode },
          ),
        );
        return null;

      case 'removeSet': {
        setValue(
          'exercises',
          getValues().exercises.map((e: { sets: any[] }, i: number) =>
            action.payload.exercise !== i
              ? e
              : {
                  ...e,
                  sets: e.sets.filter(
                    (_: any, idx: number) => idx !== action.payload.set,
                  ),
                },
          ),
        );
        return null;
      }
      case 'removeWorkoutItem': {
        setValue(
          'exercises',
          getValues().exercises.filter(
            (e: any, i: number) => i !== action.payload,
          ),
        );
        return null;
      }
      case 'removeCircuitItem': {
        const { circuitIndex, exerciseIndex } = action.payload;
        setValue(
          'exercises',
          getValues().exercises.map((e: { exercises: any[] }, i: number) =>
            i !== circuitIndex
              ? e
              : {
                  ...e,
                  exercises: e.exercises.filter(
                    (item: any, idx: number) => idx !== exerciseIndex,
                  ),
                },
          ),
        );
        return null;
      }
      case 'changeOrderInWorkout': {
        const arr = getValues().exercises;
        const { index, order } = action.payload;
        [arr[index], arr[index + order]] = [arr[index + order], arr[index]];
        setValue('exercises', arr);
        return null;
      }
      case 'addMedia': {
        if (action.payload.parentId && action.payload.subExerciseId) {
          dispatchToRefinedModel?.({
            type: 'updateSubExerciseMedia',
            payload: { ...action.payload },
          });
        } else {
          addMedia({ ...action.payload });
        }
        return null;
      }
      case 'removeMedia': {
        if (action.payload.isYoutube) {
          setValue(
            'exercises',
            getValues().exercises.map((e: any, i: number) =>
              action.payload.index !== i
                ? e
                : {
                    ...e,
                    youtubeId: undefined,
                    media: undefined,
                  },
            ),
          );
          return null;
        }
        const { index, circuitIndex } = action.payload;
        const exerciseMediaPath = (
          !isNil(circuitIndex)
            ? `${circuitIndex}.exercises.${index}.media`
            : `${index}.media`
        ) as FieldPath<LegacyWorkoutFormValues>;
        const updated = set(exercises, exerciseMediaPath, undefined);
        replace(updated);
        return null;
      }
      case 'setTimeZone':
        setValue('timeZone', action.payload);
        return null;
      case 'setPublishedAt':
        setValue('publishedAt', action.payload);
        return null;
      case 'clearSchedule':
        setValue('publishedAt', undefined);
        setValue('timeZone', undefined);
        return null;
      case 'addRunCompleteAudio': {
        const { file, index } = action.payload;
        setValue(
          'exercises',
          getValues().exercises.map((e: any, i: number) =>
            index !== i
              ? e
              : {
                  ...e,
                  media: data,
                  uploading: true,
                },
          ),
        );
        const onSuccess = (data: Media) => {
          setValue(
            'exercises',
            getValues().exercises.map((e: any, i: number) =>
              index !== i
                ? e
                : {
                    ...e,
                    media: data,
                    uploading: false,
                  },
            ),
          );
        };
        uploadAudio(file, onSuccess);
        return null;
      }
      case 'addSetAudio': {
        trackEvent({
          type: 'Audio Clip Uploaded',
        });
        const { file, exerciseIndex, setIndex } = action.payload;
        const exercise = getValues().exercises[exerciseIndex];
        update(exerciseIndex, {
          ...exercise,
          sets: exercise.sets.map((s: any, i: number) =>
            setIndex !== i ? s : { ...s, uploading: true },
          ),
        });

        const onSuccess = (data: Media) => {
          const mappedSets = exercise.sets.map((s: any, i: number) =>
            setIndex !== i ? s : { ...s, audio: data, uploading: false },
          );

          update(exerciseIndex, { ...exercise, sets: mappedSets });
        };
        uploadAudio(file, onSuccess);
        return null;
      }
      case 'clearRecommendations': {
        setExerciseSearch('');
        return null;
      }
      case 'setExerciseSearch': {
        const nextValue = action.payload || '';
        setExerciseSearch(nextValue.length >= 2 ? nextValue : '');
        return null;
      }
      case 'addFromClipboard':
        setValue('exercises', [
          ...action.payload.map((e) => ({ ...e, uuid: uuid4() })),
          ...(getValues().exercises || []),
        ]);
        return null;
      case 'removeSetAudio': {
        const { exerciseIndex, setIndex } = action.payload;
        const exercise = getValues().exercises[exerciseIndex];
        const mappedSets = exercise.sets.map((s: any, i: number) =>
          setIndex !== i ? s : { ...s, audio: undefined },
        );
        update(exerciseIndex, { ...exercise, sets: mappedSets });
        return null;
      }
      case 'removeRunCompleteAudio':
        setValue(
          'exercises',
          getValues().exercises.map((e: WorkoutItem, i: number) =>
            action.payload !== i
              ? e
              : {
                  ...e,
                  media: undefined,
                },
          ),
        );
        return null;
      case 'exerciseTitleUpdate': {
        const { circuitIndex, index, description, uuid } = action.payload;
        const path = (
          !isNil(circuitIndex)
            ? `exercises.${circuitIndex}.exercises.${index}`
            : `exercises.${index}`
        ) as FieldPath<LegacyWorkoutFormValues>;
        const currentDescription = getValues(
          `${path}.description` as FieldPath<LegacyWorkoutFormValues>,
        );
        setExerciseSearch(
          getValues(`${path}.name` as FieldPath<LegacyWorkoutFormValues>),
        );

        if (currentDescription) {
          setInputHints((prev) => ({
            ...prev,
            [uuid]: {
              ...(prev[uuid] || {}),
              description,
            },
          }));
          return null;
        }
        setValue(
          `${path}.description` as FieldPath<LegacyWorkoutFormValues>,
          description,
        );
        return null;
      }
      case 'applyHint': {
        const { circuitIndex, index, hint, uuid } = action.payload;
        const exercisePath = (
          !isNil(circuitIndex)
            ? `exercises.${circuitIndex}.exercises.${index}`
            : `exercises.${index}`
        ) as FieldPath<LegacyWorkoutFormValues>;

        const updatedhints = omit(inputHints, [uuid]);
        setInputHints(updatedhints);
        if (hint.title) {
          hint?.title &&
            setValue(
              (exercisePath + '.name') as FieldPath<LegacyWorkoutFormValues>,
              hint.title,
            );
        }
        if (hint.description) {
          setValue(
            (exercisePath +
              '.description') as FieldPath<LegacyWorkoutFormValues>,
            hint.description,
          );
        }
        return null;
      }
    }
  };

  const addTemplatesFromVideoIds = ({
    videos,
    circuitIndex,
    parentId,
  }: {
    videos: LibraryVideo[];
    circuitIndex: number | null;
    parentId?: string;
  }) => {
    if (parentId) {
      dispatchToRefinedModel?.({
        type: 'addSubExercises',
        payload: { parentId, media: videos as Media[] },
      });
      return;
    }
    const newExercises = videos.map((v) => {
      const templateType = v.type === 'short' ? 'exercise' : 'video';
      return {
        ...initTemplates[templateType],
        media: v,
        uuid: uuid4(),
        name: v.title,
        description: v.description,
      };
    });
    if (circuitIndex === null) {
      setValue('exercises', [
        ...(getValues().exercises || []),
        ...newExercises,
      ]);
      toast.success('Exercises added to workout');
      return;
    }

    const WorkoutItems = getValues().exercises || [];
    const mapped = WorkoutItems.map((e: { exercises: any }, i: number) =>
      i === circuitIndex
        ? {
            ...e,
            exercises: [...e.exercises, ...newExercises],
          }
        : e,
    );
    replace(mapped);
    toast.success('Exercises added to circuit');
  };

  const addTemplateWithMedia = ({
    media,
    circuitIndex,
    parentId,
  }: {
    media: Media;
    circuitIndex: number | null;
    parentId?: string;
  }) => {
    if (parentId) {
      dispatchToRefinedModel?.({
        type: 'addSubExercises',
        payload: { parentId, media: [media] },
      });
      return;
    }
    const templateType =
      media?.type && media.type === 'long' ? 'video' : 'exercise';
    const newExercise = {
      ...initTemplates[templateType],
      media,
      uuid: uuid4(),
      name: media.title,
      description: media.description,
    };

    if (circuitIndex === null) {
      setValue('exercises', [...(getValues().exercises || []), newExercise]);
      toast.success('Exercises added to workout');
      return;
    }

    const WorkoutItems = getValues().exercises || [];
    const mapped = WorkoutItems.map((e: { exercises: any }, i: number) =>
      i === circuitIndex
        ? {
            ...e,
            exercises: [...e.exercises, newExercise],
          }
        : e,
    );
    replace(mapped);
    toast.success('Exercises added to circuit');
  };

  const uploadMedia = (file: File) => {
    const image = file.type.startsWith('image');
    setWorkoutInfoUpload((prev) => ({
      ...prev,
      ...(image ? { image: true } : { video: true }),
    }));
    ajax
      .upload(file, influencerId)
      .then((metadata) => {
        if (image) {
          setValue('thumbnail', metadata as Media);
        } else {
          setValue('introVideo', metadata as Media);
        }
      })
      .catch(() => {
        toast.error('Something went wrong with media upload');
      })
      .finally(() => {
        setWorkoutInfoUpload((prev) => ({
          ...prev,
          ...(image ? { image: false } : { video: false }),
        }));
      });
  };

  const uploadAudio = (file: File, onSuccess: (value: Media) => void) => {
    ajax
      .upload(file, influencerId, { skipCompression: true })
      .then((metadata) => onSuccess(metadata as Media))
      .catch(() => {
        toast.error('Something went wrong with audio upload');
      });
  };

  const extendedSave = (values: LegacyWorkoutFormValues) =>
    handleSubmit(async () => {
      setSubmitting(true);
      const payload = getPayloadFromValues({
        id: workoutId,
        influencerId,
        values,
        isClass,
      });

      try {
        const body = {
          ...payload,
          isVisible: workoutId ? !!data?.workout?.isVisible : false,
          publishedAt:
            workoutId && data?.workout?.publishedAt
              ? data.workout.publishedAt
              : null,
        };

        const workout = await ajax.upsertWorkout(body);

        await ajax.attachWorkoutTags(workout.id, values.tags);

        trackWorkoutEvent(values, workout.id);
        setSubmitting(false);

        redirectFromWorkouts({
          archivedAt: workout.archivedAt,
          publishedAt: workout.publishedAt,
          programId: locationQuery?.programId,
          influencerId,
          workoutId: workout.id,
        });

        toast.success('Workout saved successfully!');
      } catch (e) {
        setSubmitting(false);
        toast.error('Something went wrong!');
      }
    });

  const extendedPublish = (values: LegacyWorkoutFormValues) =>
    handleSubmit(async () => {
      setSubmitting(true);
      try {
        if (!validatePublish(values, control)) {
          setSubmitting(false);
          return;
        }

        const payload = getPayloadFromValues({
          id: workoutId,
          influencerId,
          values,
          isClass,
        });

        const workout = await ajax.upsertWorkout({
          ...payload,
          isVisible: true,
          publishedAt: values.publishedAt || new Date().toISOString(),
          timeZone: values.timeZone,
          archivedAt: null,
        });
        await ajax.attachWorkoutTags(workout.id, values.tags);

        trackWorkoutEvent(values, workout.id);

        setSubmitting(false);
        redirectFromWorkouts({
          archivedAt: workout.archivedAt,
          publishedAt: workout.publishedAt,
          programId: locationQuery?.programId,
          influencerId,
        });
      } catch (error) {
        setSubmitting(false);
        toast.error('Something went wrong!');
      }
    });

  const unpublish = handleSubmit(async () => {
    setSubmitting(true);
    try {
      await unpublishWorkout();
      setSubmitting(false);
      redirectFromWorkouts({
        archivedAt: undefined,
        publishedAt: undefined,
        programId: locationQuery?.programId,
        influencerId,
      });
    } catch (error) {
      setSubmitting(false);
      toast.error('Something went wrong!');
    }
  });

  const archive = handleSubmit(async () => {
    setSubmitting(true);
    try {
      await ajax.archiveWorkout(workoutId || 0);

      setSubmitting(false);
      redirectFromWorkouts({
        archivedAt: new Date().toISOString(),
        publishedAt: undefined,
        programId: locationQuery?.programId,
        influencerId,
      });
    } catch (e) {
      setSubmitting(false);
      toast.error('Something went wrong!');
    }
  });

  const addMedia = ({
    index,
    circuitIndex,
    media,
  }: {
    index: number;
    circuitIndex: number | null;
    media: Media;
  }) => {
    const exerciseMediaPath = (
      !isNil(circuitIndex)
        ? `${circuitIndex}.exercises.${index}.media`
        : `${index}.media`
    ) as FieldPath<LegacyWorkoutFormValues>;
    const updated = set(exercises, exerciseMediaPath, media);

    replace(updated);
    isClass
      ? updateClassTitleDescription({
          title: media.title,
          description: media.description,
        })
      : updateTitleDescription({
          index,
          circuitIndex,
          title: media.title,
          description: media.description,
        });
  };

  const updateClassTitleDescription = ({
    title,
    description,
  }: {
    title?: string;
    description?: string;
  }) => {
    const currentName = getValues('name');
    const currentDescription = getValues('description');

    if (!currentName && title) {
      setValue('name', title);
      setValue('exercises.0.name', title);
    } else {
      setValue('exercises.0.name', currentName);
    }

    if (!currentDescription && description) {
      setValue('description', description);
      setValue('exercises.0.description', description);
    } else {
      setValue('exercises.0.description', currentDescription);
    }
  };

  const updateTitleDescription = ({
    index,
    circuitIndex,
    title,
    description,
  }: {
    index: number;
    circuitIndex: number | null;
    title?: string;
    description?: string;
  }) => {
    const exercisePath = (
      !isNil(circuitIndex)
        ? `exercises.${circuitIndex}.exercises.${index}`
        : `exercises.${index}`
    ) as FieldPath<LegacyWorkoutFormValues>;

    const currentExercise = getValues(exercisePath);
    const namePath =
      `${exercisePath}.name` as FieldPath<LegacyWorkoutFormValues>;
    const descriptionPath =
      `${exercisePath}.description` as FieldPath<LegacyWorkoutFormValues>;
    const uuid = currentExercise?.uuid;

    if (!currentExercise.name) {
      setValue(namePath, title);
    }

    if (currentExercise.name && title) {
      setInputHints((prev) => ({
        ...prev,
        [uuid]: {
          ...(prev[uuid] || {}),
          title,
        },
      }));
    }

    if (!currentExercise.description) {
      setValue(descriptionPath, description);
    }

    if (currentExercise.description && description) {
      setInputHints((prev) => ({
        ...prev,
        [uuid]: {
          ...(prev[uuid] || {}),
          description: description,
        },
      }));
    }
  };

  const intro = watch('introVideo');
  const thumbnail = watch('thumbnail');
  const exercises = watch('exercises');

  const isProgramArchived = !!programData?.program?.archivedAt;
  const isProgramPublished = !!programData?.program.publishedAt;
  const isOtp =
    programData?.program.accessType === Program.AccessType.Exclusive.value;
  const isLocked = programData?.program?.isLocked;
  const isCreatorView = auth.isInfluencer();
  const readonly = isProgramArchived && isCreatorView && isOtp;
  const disableArchiveAction =
    isProgramPublished && isCreatorView && !!isLocked && isOtp;

  return {
    type: 'fetched',
    value: {
      watch,
      setDispatchCb: setDispatchToRefinedModel,
      dispatch,
      getValues: getValues,
      workoutInfo: data?.workout || {},
      control,
      submitting,
      intro: {
        src: toVideoSrc(intro),
        loading: workoutInfoUpload.video,
      },
      thumbnail: {
        src: toImgSrcRaw(thumbnail),
        loading: workoutInfoUpload.image,
      },
      exercises,
      inputHints,
      readonly,
      disableArchiveAction,
      formErrors: formState.errors,
      newWorkoutErrors: errors,
      refinedWorkoutModel,
      exerciseSuggestions,
    },
  };
};
